<template>
  <div class="generic-faq-questions">
    <PageContent class="generic-faq-questions__content">
      <section class="generic-faq-questions__panel">
        <header class="generic-faq-questions__toolbar">
          <label class="generic-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input v-model.trim="search" type="search" placeholder="Rechercher une question" aria-label="Rechercher une question">
          </label>
          <button type="button" class="generic-primary-button" :disabled="loading" @click="openQuestion()">
            <i class="fas fa-plus-circle" aria-hidden="true"></i>
            Créer une question
          </button>
          <GenericFaqTabs />
        </header>

        <div v-if="loading" class="generic-table-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des questions…
        </div>
        <div v-else-if="loadError" class="generic-table-state generic-table-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="initialize">Réessayer</button>
        </div>
        <div v-else class="generic-table-scroll">
          <table class="generic-table">
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  :class="[
                    column.align ? `generic-table__${column.align}` : '',
                    { 'generic-column--menu-open': openColumnMenu === column.key }
                  ]"
                  :aria-sort="columnAriaSort(column)"
                  :title="column.title || null"
                >
                  <div class="generic-column-heading" :class="{ 'generic-column-heading--interactive': isColumnInteractive(column) }">
                    <button
                      v-if="isDirectSortColumn(column)"
                      type="button"
                      class="generic-column-trigger"
                      @click.stop="toggleSort(column.key)"
                    >
                      <span>{{ column.label }}</span>
                      <i :class="sortIcon(column.key)" aria-hidden="true"></i>
                    </button>
                    <button
                      v-else-if="isColumnMenuColumn(column)"
                      :id="`generic-faq-column-trigger-${column.key}`"
                      type="button"
                      class="generic-column-trigger"
                      :data-column-key="column.key"
                      :aria-expanded="openColumnMenu === column.key ? 'true' : 'false'"
                      :aria-controls="`generic-faq-column-menu-${column.key}`"
                      aria-haspopup="dialog"
                      @click.stop="toggleColumnMenu(column)"
                    >
                      <span>{{ column.label }}</span>
                      <i :class="columnMenuIcon(column)" aria-hidden="true"></i>
                      <span v-if="hasColumnFilter(column)" class="generic-column-trigger__filter-dot">
                        <span class="sr-only">Filtre actif</span>
                      </span>
                    </button>
                    <span v-else>{{ column.label }}</span>

                    <div
                      v-if="openColumnMenu === column.key"
                      :id="`generic-faq-column-menu-${column.key}`"
                      class="generic-column-popover"
                      role="dialog"
                      :aria-label="`Options de la colonne ${column.label}`"
                      @click.stop
                    >
                      <div v-if="column.sortable" class="generic-column-popover__group">
                        <span class="generic-column-popover__label">Trier</span>
                        <div class="generic-column-popover__actions">
                          <button
                            type="button"
                            :class="{ 'is-active': sort.key === column.key && sort.direction === 'asc' }"
                            @click="setSort(column.key, 'asc')"
                          >
                            {{ sortDirectionLabel(column, 'asc') }}
                          </button>
                          <button
                            type="button"
                            :class="{ 'is-active': sort.key === column.key && sort.direction === 'desc' }"
                            @click="setSort(column.key, 'desc')"
                          >
                            {{ sortDirectionLabel(column, 'desc') }}
                          </button>
                        </div>
                      </div>

                      <label v-if="column.filter" class="generic-column-popover__group">
                        <span class="generic-column-popover__label">Filtrer</span>
                        <select
                          :value="filters[column.key] || ''"
                          :aria-label="`Filtrer par ${column.label.toLowerCase()}`"
                          @change="setColumnFilter(column.key, $event.target.value)"
                        >
                          <option value="">Tous</option>
                          <option v-for="value in filterValues(column.key)" :key="String(value)" :value="String(value)">
                            {{ filterLabel(column.key, value) }}
                          </option>
                        </select>
                      </label>

                      <button
                        v-if="hasColumnState(column)"
                        type="button"
                        class="generic-column-popover__reset"
                        @click="clearColumnState(column)"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="question in paginatedQuestions"
                :key="question.uuid"
                tabindex="0"
                @click="openQuestion(question)"
                @keydown.enter.self="openQuestion(question)"
              >
                <td>{{ valueAt(question, 'type.name') }}</td>
                <td>{{ valueAt(question, 'category.label') }}</td>
                <td><span class="generic-question-title" :title="question.title">{{ question.title || '—' }}</span></td>
                <td class="generic-table__numeric">{{ Number(question.consultations_count || 0) }}</td>
                <td class="generic-table__numeric">{{ documentsCount(question) }}</td>
                <td class="generic-table__center">
                  <button
                    type="button"
                    class="generic-status-toggle"
                    :class="{ 'generic-status-toggle--enabled': toBoolean(question.is_enabled) }"
                    :disabled="pendingUuid === question.uuid"
                    :aria-label="toBoolean(question.is_enabled) ? 'Désactiver la question' : 'Activer la question'"
                    @click.stop="toggleEnabled(question)"
                  >
                    <span aria-hidden="true"></span>
                    {{ toBoolean(question.is_enabled) ? 'Active' : 'Inactive' }}
                  </button>
                </td>
                <td class="generic-table__actions">
                  <button type="button" class="generic-delete-button" title="Supprimer" @click.stop="confirmDelete(question)">
                    <i class="far fa-trash-alt" aria-hidden="true"></i><span class="sr-only">Supprimer</span>
                  </button>
                </td>
              </tr>
              <tr v-if="!paginatedQuestions.length">
                <td colspan="7" class="generic-table-state">Aucune question ne correspond à votre recherche.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer v-if="!loading && !loadError" class="generic-table-footer">
          <span>{{ rangeLabel }}</span>
          <div v-if="totalPages > 1">
            <button type="button" :disabled="page === 1" @click="page--"><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
            <span>Page {{ page }} / {{ totalPages }}</span>
            <button type="button" :disabled="page === totalPages" @click="page++"><i class="fas fa-chevron-right" aria-hidden="true"></i></button>
          </div>
        </footer>
      </section>
    </PageContent>

    <t-modal name="question-form" variant="articleForm" hide-close-button>
      <MVPQuestionsForm
        v-if="currentQuestion"
        :question="currentQuestion"
        :questionKey="currentQuestion.uuid"
        :orderedUuids="orderedUuids"
        :currentIndex="currentIndex"
        :disable-creation-notification="true"
        @refresh="loadQuestions"
        @navigate="handleNavigate"
      />
    </t-modal>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import MVPQuestionsForm from '@/pages/Questions/Form.vue'
import GenericFaqTabs from '@/pages/dashboard/GenericFaqTabs.vue'
import {
  NOSDROITS_DOMAIN_UUID,
  selectInternalSourceDomain
} from '@/services/internalGenericContents.service'

const DEFAULT_QUESTION = {
  uuid: null,
  title: '',
  response: '',
  type: { uuid: null },
  category: { uuid: null },
  is_enabled: true,
  keywords: [],
  files: []
}

const FAQ_COLUMNS = [
  { key: 'type', label: 'Type', sortable: true, filter: true },
  { key: 'category', label: 'Catégorie', sortable: true, filter: true },
  { key: 'title', label: 'Question', sortable: true },
  { key: 'consultations_count', label: 'Lectures', title: 'Cumul des lectures sur tous les domaines', sortable: true, align: 'numeric' },
  { key: 'documents_count', label: 'PJ', align: 'numeric' },
  { key: 'is_enabled', label: 'Statut', sortable: true, filter: true, align: 'center' },
  { key: 'delete', label: 'Suppression', align: 'actions' }
]

export default {
  name: 'app-dashboard-generic-faq-questions',
  components: { PageContent, MVPQuestionsForm, GenericFaqTabs },
  data () {
    return {
      questions: [],
      loading: true,
      loadError: '',
      search: '',
      columns: FAQ_COLUMNS,
      filters: {},
      sort: { key: 'title', direction: 'asc' },
      openColumnMenu: null,
      page: 1,
      perPage: 25,
      pendingUuid: null,
      currentQuestion: null,
      orderedUuids: [],
      currentIndex: 0
    }
  },
  computed: {
    filteredQuestions () {
      const search = this.search.toLocaleLowerCase('fr')
      const questions = this.questions.filter(question => {
        if (!this.matchesColumnFilters(question)) return false
        if (!search) return true
        const haystack = `${question.title || ''} ${this.stripHtml(question.response)}`.toLocaleLowerCase('fr')
        return haystack.includes(search)
      })

      if (!this.sort.key || !this.sort.direction) return questions
      const factor = this.sort.direction === 'asc' ? 1 : -1
      return questions.sort((left, right) => {
        const a = this.sortValue(left, this.sort.key)
        const b = this.sortValue(right, this.sort.key)
        if (a === b) return 0
        if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b, 'fr', { sensitivity: 'base' }) * factor
        return (a > b ? 1 : -1) * factor
      })
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.filteredQuestions.length / this.perPage))
    },
    paginatedQuestions () {
      const start = (this.page - 1) * this.perPage
      return this.filteredQuestions.slice(start, start + this.perPage)
    },
    rangeLabel () {
      if (!this.filteredQuestions.length) return '0 question'
      const start = (this.page - 1) * this.perPage + 1
      const end = Math.min(this.page * this.perPage, this.filteredQuestions.length)
      return `${start}–${end} sur ${this.filteredQuestions.length} questions`
    }
  },
  watch: {
    search () { this.page = 1 },
    totalPages (value) {
      if (this.page > value) this.page = value
    }
  },
  created () {
    this.initialize()
  },
  mounted () {
    document.addEventListener('click', this.closeColumnMenu)
    document.addEventListener('keydown', this.handleColumnMenuKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeColumnMenu)
    document.removeEventListener('keydown', this.handleColumnMenuKeydown)
  },
  methods: {
    async initialize () {
      this.loading = true
      this.loadError = ''
      try {
        await selectInternalSourceDomain(this.$store, NOSDROITS_DOMAIN_UUID)
        await this.loadQuestions()
      } catch (error) {
        this.loadError = 'Impossible de charger les questions Code du travail.'
        this.loading = false
      }
    },
    async loadQuestions () {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await http.get('/internal/generic-contents/faq/questions')
        this.questions = Array.isArray(data) ? data : []
      } catch (error) {
        this.loadError = 'Impossible de charger les questions Code du travail.'
      } finally {
        this.loading = false
      }
    },
    valueAt (question, path) {
      const value = path.split('.').reduce((current, key) => current && current[key], question)
      return value || '—'
    },
    stripHtml (html) {
      if (!html) return ''
      const container = document.createElement('div')
      container.innerHTML = String(html)
      return container.textContent || container.innerText || ''
    },
    documentsCount (question) {
      const documents = question.documents || question.files
      return Array.isArray(documents) ? documents.length : 0
    },
    toBoolean (value) {
      return [true, 1, '1', 'true'].includes(value)
    },
    sortValue (question, key) {
      if (key === 'type') return this.valueAt(question, 'type.name')
      if (key === 'category') return this.valueAt(question, 'category.label')
      if (key === 'is_enabled') return this.toBoolean(question.is_enabled) ? 1 : 0
      if (key === 'consultations_count') return Number(question.consultations_count || 0)
      return String(question.title || '')
    },
    isColumnInteractive (column) {
      return Boolean(column && (column.sortable || column.filter))
    },
    isDirectSortColumn (column) {
      return Boolean(column && column.sortable && !column.filter)
    },
    isColumnMenuColumn (column) {
      return Boolean(column && column.filter)
    },
    toggleSort (key) {
      const direction = this.sort.key === key && this.sort.direction === 'asc' ? 'desc' : 'asc'
      this.sort = { key, direction }
      this.closeColumnMenu()
    },
    sortIcon (key) {
      if (this.sort.key !== key || !this.sort.direction) return 'fas fa-sort'
      return this.sort.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    toggleColumnMenu (column) {
      if (!this.isColumnMenuColumn(column)) return
      this.openColumnMenu = this.openColumnMenu === column.key ? null : column.key
    },
    closeColumnMenu () {
      this.openColumnMenu = null
    },
    handleColumnMenuKeydown (event) {
      if (event.key !== 'Escape' || !this.openColumnMenu) return
      const columnKey = this.openColumnMenu
      this.closeColumnMenu()
      this.$nextTick(() => {
        const trigger = this.$el.querySelector(`[data-column-key="${columnKey}"]`)
        if (trigger) trigger.focus()
      })
    },
    setSort (key, direction) {
      this.sort = { key, direction }
    },
    setColumnFilter (key, value) {
      if (value) this.$set(this.filters, key, value)
      else this.$delete(this.filters, key)
      this.page = 1
    },
    questionColumnValue (question, key) {
      if (key === 'type') return this.valueAt(question, 'type.name')
      if (key === 'category') return this.valueAt(question, 'category.label')
      if (key === 'is_enabled') return this.toBoolean(question.is_enabled) ? 'enabled' : 'disabled'
      return question[key]
    },
    matchesColumnFilters (question) {
      return Object.keys(this.filters).every(key => {
        const expected = this.filters[key]
        return !expected || String(this.questionColumnValue(question, key)) === String(expected)
      })
    },
    filterValues (key) {
      if (key === 'is_enabled') return ['enabled', 'disabled']
      const values = new Set()
      this.questions.forEach(question => {
        const value = this.questionColumnValue(question, key)
        if (value !== null && value !== undefined && value !== '') values.add(String(value))
      })
      return [...values].sort((left, right) => this.filterLabel(key, left).localeCompare(this.filterLabel(key, right), 'fr'))
    },
    filterLabel (key, value) {
      if (key === 'is_enabled') return value === 'enabled' ? 'Active' : 'Inactive'
      return value
    },
    hasColumnFilter (column) {
      return Boolean(column && this.filters[column.key])
    },
    hasColumnState (column) {
      return Boolean(column && ((this.sort.key === column.key && this.sort.direction) || this.hasColumnFilter(column)))
    },
    clearColumnState (column) {
      if (!column) return
      if (this.sort.key === column.key) this.sort = { key: null, direction: null }
      this.$delete(this.filters, column.key)
      this.page = 1
    },
    sortDirectionLabel (column, direction) {
      if (['type', 'category', 'title', 'is_enabled'].includes(column.key)) {
        return direction === 'asc' ? 'A → Z' : 'Z → A'
      }
      return direction === 'asc' ? 'Croissant' : 'Décroissant'
    },
    columnMenuIcon (column) {
      if (this.sort.key === column.key && this.sort.direction) {
        return this.sort.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
      }
      if (this.hasColumnState(column)) return 'fas fa-filter'
      return 'fas fa-chevron-down'
    },
    columnAriaSort (column) {
      if (!column.sortable || this.sort.key !== column.key || !this.sort.direction) return null
      return this.sort.direction === 'asc' ? 'ascending' : 'descending'
    },
    normalizeQuestion (question) {
      const normalized = Object.assign({}, DEFAULT_QUESTION, question || {})
      normalized.type = normalized.type || { uuid: null }
      normalized.category = normalized.category || { uuid: null }
      normalized.keywords = normalized.keywords || []
      normalized.files = normalized.files || []
      return normalized
    },
    openQuestion (question = null) {
      this.currentQuestion = this.normalizeQuestion(question)
      this.orderedUuids = question ? this.filteredQuestions.map(item => item.uuid) : []
      this.currentIndex = question ? Math.max(0, this.orderedUuids.indexOf(question.uuid)) : 0
      this.$modal.show('question-form', this.currentQuestion)
    },
    async handleNavigate ({ direction }) {
      const nextIndex = this.currentIndex + (direction || 0)
      if (nextIndex < 0 || nextIndex >= this.orderedUuids.length) return
      try {
        const { data } = await http.get(`/faq/${this.orderedUuids[nextIndex]}`)
        this.currentQuestion = this.normalizeQuestion(data)
      } catch (error) {
        this.currentQuestion = this.normalizeQuestion(this.questions.find(question => question.uuid === this.orderedUuids[nextIndex]))
      }
      this.currentIndex = nextIndex
    },
    async toggleEnabled (question) {
      if (this.pendingUuid) return
      const enabled = !this.toBoolean(question.is_enabled)
      this.pendingUuid = question.uuid
      try {
        await http.post(`/faq/${question.uuid}`, { is_enabled: enabled ? 1 : 0 })
        question.is_enabled = enabled
        this.$notify({ group: 'maviepro-success', text: `Question ${enabled ? 'activée' : 'désactivée'}.` })
      } catch (error) {
        this.$notify({ group: 'maviepro-error', text: 'Impossible de modifier le statut de la question.' })
      } finally {
        this.pendingUuid = null
      }
    },
    confirmDelete (question) {
      this.$buefy.dialog.confirm({
        title: 'Supprimer cette question ?',
        message: `La question « ${question.title} » sera définitivement supprimée.`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        type: 'is-danger',
        onConfirm: () => this.deleteQuestion(question)
      })
    },
    async deleteQuestion (question) {
      this.pendingUuid = question.uuid
      try {
        await http.delete(`/faq/${question.uuid}`)
        this.questions = this.questions.filter(item => item.uuid !== question.uuid)
        this.$notify({ group: 'maviepro-success', text: 'Question supprimée.' })
      } catch (error) {
        this.$notify({ group: 'maviepro-error', text: 'Impossible de supprimer cette question.' })
      } finally {
        this.pendingUuid = null
      }
    }
  }
}
</script>

<style scoped>
.generic-faq-questions { background: #f3f4f6; font-size: 14px; height: 100%; min-height: 0; min-width: 0; overflow: hidden; width: 100%; }
.generic-faq-questions__content { display: flex; flex-direction: column; height: calc(100% - 40px); min-height: 0; }
.generic-faq-questions__panel { background: #fff; border: 1px solid #bfdbfe; border-radius: 14px; box-shadow: 0 4px 12px rgba(15, 23, 42, .06); display: flex; flex: 1 1 auto; flex-direction: column; max-width: 100%; min-height: 0; min-width: 0; overflow: hidden; }
.generic-faq-questions__toolbar { align-items: center; display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 18px 24px; padding: 22px 28px; }
.generic-primary-button { align-items: center; background: #2563eb; border: 0; border-radius: 10px; color: #fff; display: inline-flex; font-size: 14px; font-weight: 400; gap: 8px; justify-content: center; min-width: 210px; padding: 14px 18px; white-space: nowrap; }
.generic-primary-button:hover:not(:disabled) { background: #1d4ed8; }
.generic-search { align-items: center; background: #fff; border: 1px solid #d1d5db; border-radius: 9px; display: flex; flex: 1 1 260px; max-width: 360px; min-width: 240px; padding: 0 14px; }
.generic-search:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, .12); }
.generic-search i { color: #9ca3af; }
.generic-search input { border: 0; box-shadow: none; font-size: 14px; min-width: 0; outline: none; padding: 11px; width: 100%; }
.generic-table-scroll { contain: paint; flex: 1 1 auto; max-width: 100%; min-height: 0; min-width: 0; overflow: auto; width: 100%; }
.generic-table { border-collapse: separate; border-spacing: 0; min-width: 1000px; table-layout: fixed; width: 100%; }
.generic-table th { background: #f9fafb; color: #4b5563; font-size: 14px; font-weight: 700; letter-spacing: .03em; padding: 12px 14px; position: sticky; text-align: left; text-transform: uppercase; top: 0; vertical-align: top; z-index: 2; }
.generic-table th.generic-column--menu-open { z-index: 5; }
.generic-column-heading { align-items: center; display: flex; min-height: 24px; position: relative; }
.generic-column-trigger { align-items: center; background: transparent; border: 0; color: inherit; cursor: pointer; display: inline-flex; font: inherit; gap: 7px; letter-spacing: inherit; padding: 3px 0; text-align: left; text-transform: inherit; }
.generic-column-trigger:hover, .generic-column-trigger:focus-visible { color: #2563eb; }
.generic-column-trigger:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
.generic-column-trigger i { color: #9ca3af; }
.generic-column-trigger__filter-dot { background: #2563eb; border: 2px solid #f9fafb; border-radius: 999px; height: 8px; margin-left: -4px; width: 8px; }
.generic-column-popover { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 12px 28px rgba(15, 23, 42, .18); color: #111827; font-size: 0.75rem; font-weight: 400; left: 0; letter-spacing: normal; min-width: 230px; padding: 12px; position: absolute; text-align: left; text-transform: none; top: calc(100% + 8px); z-index: 10; }
.generic-table th.generic-table__center .generic-column-popover { left: auto; right: 0; }
.generic-column-popover__group { display: flex; flex-direction: column; gap: 7px; margin: 0 0 12px; }
.generic-column-popover__group:last-child { margin-bottom: 0; }
.generic-column-popover__label { color: #4b5563; font-size: 0.75rem; font-weight: 700; }
.generic-column-popover__actions { display: grid; gap: 6px; grid-template-columns: 1fr 1fr; }
.generic-column-popover button, .generic-column-popover select { background: #fff; border: 1px solid #d1d5db; border-radius: 6px; color: #111827; font: inherit; min-height: 34px; padding: 7px 9px; text-transform: none; }
.generic-column-popover select { width: 100%; }
.generic-column-popover__actions button { cursor: pointer; }
.generic-column-popover__actions button:hover, .generic-column-popover__actions button.is-active { background: #eff6ff; border-color: #2563eb; color: #2563eb; }
.generic-column-popover__reset { color: #2563eb !important; cursor: pointer; width: 100%; }
.generic-column-popover__reset:hover { background: #eff6ff; border-color: #bfdbfe; }
.generic-table td { border-top: 1px solid #e5e7eb; box-sizing: border-box; color: #111827; height: 52px; line-height: 1.25rem; padding: 6px 14px; vertical-align: middle; }
.generic-table tbody tr { cursor: pointer; height: 52px; transition: background .15s ease; }
.generic-table tbody tr:hover { background: #f9fafb; }
.generic-table tbody tr:focus-visible { outline: 2px solid #2563eb; outline-offset: -2px; }
.generic-question-title { display: -webkit-box; line-height: 1.25rem; max-height: 2.5rem; max-width: 560px; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.generic-table__numeric { text-align: right !important; width: 95px; }
.generic-table__center { text-align: center !important; width: 130px; }
.generic-table__actions { text-align: center !important; width: 100px; }
.generic-table th.generic-table__numeric .generic-column-heading { justify-content: flex-end; }
.generic-table th.generic-table__center .generic-column-heading, .generic-table th.generic-table__actions .generic-column-heading { justify-content: center; }
.generic-table td.generic-table__actions { padding: 0; white-space: nowrap; }
.generic-table__actions > button { align-items: center; background: transparent; border: 0; color: #2563eb; display: flex; font-size: 1.125rem; justify-content: center; min-height: 52px; width: 100%; }
.generic-table__actions > .generic-delete-button { color: #ef4444; }
.generic-status-toggle { align-items: center; background: #f9fafb; border: 0; border-radius: 999px; color: #4b5563; display: inline-flex; font-size: 14px; font-weight: 600; gap: 6px; padding: 5px 10px; }
.generic-status-toggle > span { background: #4b5563; border-radius: 50%; height: 7px; width: 7px; }
.generic-status-toggle--enabled { background: #dcfce7; color: #166534; }
.generic-status-toggle--enabled > span { background: #22c55e; }
.generic-table-state { color: #4b5563; flex: 1 1 auto; padding: 48px !important; text-align: center !important; }
.generic-table-state--error { color: #b91c1c; }
.generic-table-state--error button { background: transparent; border: 0; color: #2563eb; margin-left: 8px; }
.generic-table-footer { align-items: center; border-top: 1px solid #e5e7eb; color: #4b5563; display: flex; flex: 0 0 auto; font-size: 12px; justify-content: space-between; padding: 12px 24px; }
.generic-table-footer > div { align-items: center; display: flex; gap: 10px; }
.generic-table-footer button { background: #fff; border: 1px solid #d1d5db; border-radius: 6px; color: #4b5563; height: 30px; width: 32px; }
.generic-table-footer button:disabled { color: #e5e7eb; }
@media (max-width: 1100px) {
  .generic-faq-questions__toolbar { align-items: stretch; flex-direction: column; }
  .generic-search { flex: none; max-width: 100%; min-width: 0; width: 100%; }
}
</style>
